import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { DocumentTemplateForm } from '@/components/document-template-form';
import { JournalEntryForm } from '@/components/journal-entry-form';
import { EmptyState } from '@/components/page-header';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { takeTemplateSeed, useRecurringTemplate } from '@/lib/recurring';
import {
  isDocumentTemplateKind,
  takeDocumentTemplateSeed,
  type DocumentTemplateKind,
} from '@/lib/recurring-documents';

export const Route = createFileRoute('/accounting/recurring/new')({
  component: NewTemplate,
  validateSearch: (
    s: Record<string, unknown>,
  ): { copyOf?: string; kind?: DocumentTemplateKind } => ({
    ...(typeof s['copyOf'] === 'string' ? { copyOf: s['copyOf'] } : {}),
    ...(isDocumentTemplateKind(s['kind']) ? { kind: s['kind'] } : {}),
  }),
});

/**
 * Screen A7: new recurring template. Journal entries by default; `kind` (invoice, bill, expense)
 * opens the document template form; `copyOf` duplicates; "Make recurring" seeds the form.
 */
function NewTemplate() {
  const orgId = useOrgId();
  const tr = useT();
  const { copyOf, kind } = Route.useSearch();
  const source = useRecurringTemplate(copyOf);
  const [seed] = useState(() => (kind ? null : takeTemplateSeed()));
  const [docSeed] = useState(() => (kind ? takeDocumentTemplateSeed(kind) : null));
  if (!orgId) return <EmptyState title={tr('shell.selectClient')} />;
  if (copyOf && source.isPending)
    return <p className="text-sm text-muted-foreground">{tr('common.loading')}</p>;
  if (copyOf && source.isError)
    return (
      <EmptyState
        title={tr('accountingPages.recurringTemplate.couldNotOpen')}
        hint={source.error.message}
      />
    );
  const docKind = copyOf ? source.data?.kind : kind;
  if (isDocumentTemplateKind(docKind))
    return (
      <DocumentTemplateForm
        key={`${orgId}-${copyOf ?? docKind}`}
        kind={docKind}
        template={source.data ?? null}
        duplicate={Boolean(copyOf)}
        seed={docSeed}
      />
    );
  return (
    <JournalEntryForm
      key={`${orgId}-${copyOf ?? 'new'}`}
      variant={{
        kind: 'template',
        template: source.data ?? null,
        duplicate: Boolean(copyOf),
        seed,
      }}
    />
  );
}
